import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

interface UseAutoSaveOptions {
  onSave: () => Promise<boolean | void>;
  debounceMs?: number;
  successMessage?: string;
  errorMessage?: string;
}

export function useAutoSave({
  onSave,
  debounceMs = 2000,
  successMessage = "Changes saved automatically",
  errorMessage = "Failed to save changes automatically",
}: UseAutoSaveOptions) {
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const savedIndicatorRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveAttemptRef = useRef<number>(0);
  const savePromiseRef = useRef<Promise<boolean | void> | null>(null);
  const inputActiveRef = useRef<boolean>(false);

  // Track focus and blur events on input elements
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        inputActiveRef.current = true;
      }
    };

    const handleBlur = (e: FocusEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        inputActiveRef.current = false;
        // If form is dirty, trigger save after blur
        if (isDirty) {
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
          saveTimeoutRef.current = setTimeout(() => {
            handleSave();
          }, 300); // Short delay after blur before saving
        }
      }
    };

    document.addEventListener("focus", handleFocus, true);
    document.addEventListener("blur", handleBlur, true);

    return () => {
      document.removeEventListener("focus", handleFocus, true);
      document.removeEventListener("blur", handleBlur, true);
    };
  }, [isDirty]);

  // Mark the form as dirty
  const setDirty = () => {
    setIsDirty(true);
  };

  // Cancel any pending auto-save operation
  const cancelSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    setIsDirty(false);
  };

  // Handle the save action
  const handleSave = async () => {
    // Don't save if we're already saving or if the form isn't dirty
    if (isSaving || !isDirty) return;

    // Record when we started this save attempt
    const saveAttemptTime = Date.now();
    lastSaveAttemptRef.current = saveAttemptTime;

    setIsSaving(true);
    try {
      savePromiseRef.current = onSave();
      const result = await savePromiseRef.current;

      // Only show success and reset dirty if this is the latest save attempt
      if (saveAttemptTime === lastSaveAttemptRef.current) {
        // Only show toast if save was successful AND resulted in actual changes
        if (result === true) {
          // Update saved indicator without toast
          setLastSaved(new Date());
          setIsDirty(false);
          try {
            const el = document.getElementById("editor-saved-indicator");
            if (el) {
              el.classList.remove("opacity-0");
              el.classList.add("opacity-100");
              setTimeout(() => {
                el.classList.add("opacity-0");
                el.classList.remove("opacity-100");
              }, 1500);
            }
          } catch {}
        } else if (result === false) {
          // If the save function explicitly returned false, show an error
          toast({
            variant: "destructive",
            title: errorMessage,
            description:
              "The server couldn't process your changes. Please try again.",
          });
        } else {
          // For undefined/void result (no changes made but not an error), just reset dirty state
          setLastSaved(new Date());
          setIsDirty(false);
        }
      }
    } catch (error) {
      // Only show error if this is the latest save attempt
      if (saveAttemptTime === lastSaveAttemptRef.current) {
        toast({
          variant: "destructive",
          title: errorMessage,
          description:
            error instanceof Error
              ? error.message
              : "An unexpected error occurred",
        });
      }
    } finally {
      // Only reset saving state if this is the latest save attempt
      if (saveAttemptTime === lastSaveAttemptRef.current) {
        savePromiseRef.current = null;
        setIsSaving(false);
      }
    }
  };

  // Force an immediate save
  const forceSave = async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    await handleSave();
  };

  // Auto-save when form becomes dirty
  useEffect(() => {
    if (isDirty) {
      console.log("Form is dirty, scheduling auto-save");

      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Only set a timeout if no input is currently active
      if (!inputActiveRef.current) {
        // Set a new timeout to save after the debounce period
        saveTimeoutRef.current = setTimeout(() => {
          console.log("Auto-save timeout triggered");
          handleSave();
        }, debounceMs);
      }
    }

    return () => {
      // Clean up the timeout on unmount
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [isDirty, debounceMs]);

  return {
    isSaving,
    isDirty,
    lastSaved,
    setDirty,
    handleSave,
    forceSave,
    cancelSave,
  };
}
